// ══════════════════════════════════════════════════════════════════════════════
// SELDOR — TopProductsChart
// Horizontal bar chart showing top profitable products
// ══════════════════════════════════════════════════════════════════════════════

import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Cell
} from 'recharts';
import { useTheme } from '@contexts/ThemeContext';
import { useLang } from '@contexts/LangContext';
import { Card } from '@components/ui/Primitives';

export function TopProductsChart({ products = [] }) {
  const { theme } = useTheme();
  const { t, lang } = useLang();

  const chartData = [...products]
    .sort((a, b) => parseFloat(b.profit_margin) - parseFloat(a.profit_margin))
    .slice(0, 8)
    .map(p => ({
      name: lang === 'ar' ? p.name_ar : p.name_en,
      margin: parseFloat(p.profit_margin),
      sellingPrice: parseFloat(p.selling_price),
      costPrice: parseFloat(p.cost_price)
    }));

  const tooltipStyle = {
    background: theme.bgCard,
    border: `1px solid ${theme.border}`,
    borderRadius: 8,
    fontSize: 12,
    padding: '8px 12px'
  };

  const getBarColor = (margin) => {
    if (margin >= 75) return theme.success;
    if (margin >= 65) return theme.primary;
    if (margin >= 55) return theme.warning;
    return theme.danger;
  };

  return (
    <Card style={{ padding: 22 }}>
      <h4 style={{
        margin: '0 0 16px',
        fontSize: 14,
        fontWeight: 700,
        color: theme.text
      }}>
        {t.topProducts}
      </h4>
      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={chartData} layout="vertical">
          <CartesianGrid strokeDasharray="3 3" stroke={theme.border} />
          <XAxis
            type="number"
            tick={{ fill: theme.textTertiary, fontSize: 10 }}
            axisLine={false}
            tickLine={false}
            domain={[0, 100]}
          />
          <YAxis
            dataKey="name"
            type="category"
            tick={{ fill: theme.textSecondary, fontSize: 10 }}
            axisLine={false}
            tickLine={false}
            width={110}
          />
          <Tooltip
            contentStyle={tooltipStyle}
            formatter={(value) => `${value}%`}
          />
          <Bar dataKey="margin" radius={[0, 4, 4, 0]} name={t.grossMargin}>
            {chartData.map((entry, i) => (
              <Cell key={i} fill={getBarColor(entry.margin)} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Card>
  );
}
